import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Search, Sparkles, Coffee, Leaf } from "lucide-react";
import CafeCard from "@/components/CafeCard";
import CitiesGrid from "@/components/CitiesGrid";
import NewsletterSignup from "@/components/NewsletterSignup";
import ActivityFeed from "@/components/ActivityFeed";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: "Find the cafés worth walking to",
    description: "A handcrafted directory of independent cafés, roasteries, and specialty bean shops across the US and Canada — curated by people who actually drink the coffee.",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cafes/featured");
        setFeatured(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1554118811-1e0d58224f24?q=80&w=2000&auto=format&fit=crop"
            alt="A cozy cafe interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1E16]/80 via-[#2C1E16]/45 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-32 w-full">
          <div className="max-w-3xl">
            <p className="text-[#F9F6F0]/80 uppercase tracking-[0.3em] text-xs mb-6">
              For people who care where the coffee comes from
            </p>
            <h1 className="font-serif text-[#F9F6F0] text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Find the cafés <em className="font-normal italic">worth</em> walking to.
            </h1>
            <p className="mt-6 text-[#F9F6F0]/90 text-lg max-w-xl leading-relaxed">
              A handcrafted directory of independent cafés, roasteries, and specialty bean shops — curated and reviewed by people who actually drink the coffee.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0] px-7 h-12 text-sm tracking-wide"
              >
                <Link to="/directory" data-testid="hero-cta-directory">
                  Explore the directory <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-sm border-[#F9F6F0]/60 bg-transparent text-[#F9F6F0] hover:bg-[#F9F6F0] hover:text-[#2C1E16] h-12 px-7"
              >
                <Link to="/submit" data-testid="hero-cta-submit">Submit a spot</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Value propositions — Bento */}
      <section className="bg-grain py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-6 md:gap-8">
            <div className="md:col-span-7 border border-border bg-card p-10 rounded-sm flex flex-col justify-between min-h-[280px]">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">01 / The promise</p>
                <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-[1.05]">
                  Specialty coffee, <br />without the algorithm.
                </h2>
              </div>
              <p className="text-[#786B63] leading-relaxed max-w-md mt-6">
                No sponsored placements. No five-star inflation. Just spots vetted by people who know what a properly pulled shot tastes like.
              </p>
            </div>

            <div className="md:col-span-5 border border-border bg-[#2C1E16] text-[#F9F6F0] p-10 rounded-sm flex flex-col justify-between min-h-[280px]">
              <Coffee className="h-7 w-7 text-[#C05A46]" strokeWidth={1.5} />
              <div>
                <h3 className="font-serif text-3xl leading-tight">Cafés & roasteries, mapped.</h3>
                <p className="text-[#F9F6F0]/70 text-sm mt-3 leading-relaxed">
                  Browse by neighborhood. See exactly where to walk to find your next morning ritual.
                </p>
              </div>
            </div>

            <div className="md:col-span-4 border border-border bg-card p-8 rounded-sm min-h-[220px]">
              <Leaf className="h-6 w-6 text-[#C05A46]" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mt-5">Bean shops & roasters</h3>
              <p className="text-sm text-[#786B63] mt-2 leading-relaxed">
                High-quality whole beans from independent roasters — the ones worth grinding at home.
              </p>
            </div>

            <div className="md:col-span-4 border border-border bg-card p-8 rounded-sm min-h-[220px]">
              <Search className="h-6 w-6 text-[#C05A46]" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mt-5">Honest reviews</h3>
              <p className="text-sm text-[#786B63] mt-2 leading-relaxed">
                Written by drinkers who care about origin, process, and how the cortado actually pours.
              </p>
            </div>

            <div className="md:col-span-4 border border-border bg-[#C05A46] text-[#F9F6F0] p-8 rounded-sm min-h-[220px]">
              <Sparkles className="h-6 w-6" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mt-5">Community-curated</h3>
              <p className="text-sm text-[#F9F6F0]/85 mt-2 leading-relaxed">
                Found a hidden gem? Submit it. Every great list starts with someone paying attention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Highlights</p>
              <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Loved by the community.</h2>
            </div>
            <Link
              to="/directory"
              className="text-sm tracking-wide text-[#C05A46] hover:underline inline-flex items-center gap-2"
              data-testid="featured-view-all"
            >
              View the whole directory <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="border border-border bg-card aspect-[4/5] rounded-sm animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featured.map((c) => <CafeCard key={c.id} cafe={c} />)}
            </div>
          )}
        </div>
      </section>

      {/* Activity feed */}
      <ActivityFeed />


      {/* Cities */}
      <CitiesGrid />

      {/* Newsletter */}
      <NewsletterSignup variant="band" />

      {/* CTA Band */}
      <section className="border-t border-border bg-[#F2EBE1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">For the obsessed</p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">
              Know a place that should be on this list?
            </h2>
          </div>
          <div className="md:justify-self-end">
            <p className="text-[#786B63] leading-relaxed mb-6 max-w-md">
              Help build the directory. Submit your favorite tucked-away cafe or roaster — we'll do the rest.
            </p>
            <Button
              asChild
              size="lg"
              className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0] h-12 px-8"
            >
              <Link to="/submit" data-testid="cta-submit">
                <MapPin className="h-4 w-4 mr-2" strokeWidth={1.5} /> Submit a café
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
